import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import {
  AppConfig,
  ApprovalFilter,
  ApprovalState,
  ApprovalTempFilter,
  DocumentListFilters,
  FavoriteList,
  MyInfo,
  NotificationState,
  OpenState,
  OrgChartState,
  OrgChartStateGlobal,
  SalesData,
  SocialLayoutState,
  SocialState,
  VersionInfo,
  WebApprovalState,
} from '@/types/api';
import {
  INIT_AI_MARKETING_DATA_STATE,
  INIT_APP_SETTINGS,
  INIT_APPROVAL_STATE,
  INIT_MY_INFO,
  INIT_NOTIFICATION_STATE,
  INIT_ORG_CHART_STATE,
  INIT_ORG_CHART_STATE_GLOBAL,
  INIT_SALES_DATA,
  INIT_SALES_OPEN_STATE,
  INIT_SOCIAL_LAYOUT_STATE,
  INIT_SOCIAL_STATE,
  INIT_VERSION_INFO,
  INIT_WEB_APPROVAL_FILTER_STATE,
  INIT_WEB_APPROVAL_STATE,
  INIT_WEB_APPROVAL_TEMP_FILTER_STATE,
  INIT_WEB_DOCUMENT_FILTER_STATE,
} from '@/recoil/init_data';
import { AIMarketingParameterData } from '@/components/AIMarketingExpense/types';
import {
  atomWithLocalStorage,
  atomWithSessionStorage,
} from '@/recoil/jotaiStorage';

export const MyInfoState = atomWithLocalStorage<MyInfo>(
  'myInfoState',
  INIT_MY_INFO,
);

export const approvalState = atomWithSessionStorage<ApprovalState>(
  'approvalState',
  INIT_APPROVAL_STATE,
);

export const webApprovalState = atom<WebApprovalState>(INIT_WEB_APPROVAL_STATE);

export const approvalFilter = atomWithSessionStorage<ApprovalFilter>(
  'approvalFilterState',
  INIT_WEB_APPROVAL_FILTER_STATE,
);

export const INIT_FILTER = {
  ...INIT_WEB_APPROVAL_FILTER_STATE,
  id: 1,
  endAuthoredAt: [],
};

export const approvalTempFilter = atom<ApprovalTempFilter>(
  INIT_WEB_APPROVAL_TEMP_FILTER_STATE,
);

export const initTempFilter = (
  filters: ApprovalFilter | FavoriteList,
): ApprovalTempFilter => ({
  types: filters.types,
  authorPassportIds: filters.authorPassportIds,
  approverPassportIds: filters.approverPassportIds,
  currentApproverPassportIds: filters.currentApproverPassportIds,
  coApproverPassportIds: filters.coApproverPassportIds,
  finalApproverPassportIds: filters.finalApproverPassportIds,
});

export const appSettingState = atomWithLocalStorage<AppConfig>(
  'appConfigState',
  INIT_APP_SETTINGS,
);

export const currencyState = atomWithLocalStorage<boolean>(
  'currencyConfigState',
  true,
  false,
);

export const typeCurrencyState = atomWithLocalStorage<boolean>(
  'typeCurrencyConfigState',
  true,
  false,
);

export const salesDataState = atom<SalesData>(INIT_SALES_DATA);

export const appVersionState = atom<VersionInfo>(INIT_VERSION_INFO);

export const salesOpenState = atom<OpenState>(INIT_SALES_OPEN_STATE);

export const socialState = atom<SocialState>(INIT_SOCIAL_STATE);

export const notificationState = atom<NotificationState>(
  INIT_NOTIFICATION_STATE,
);

export const socialLayoutState = atom<SocialLayoutState>(
  INIT_SOCIAL_LAYOUT_STATE,
);

export const orgLayoutState = atom<SocialLayoutState>(INIT_SOCIAL_LAYOUT_STATE);

export const orgChartState = atom<OrgChartState>(INIT_ORG_CHART_STATE);

export const orgChartStateGlobal = atom<OrgChartStateGlobal>(
  INIT_ORG_CHART_STATE_GLOBAL,
);

export const documentFilterState = atom<DocumentListFilters>(
  INIT_WEB_DOCUMENT_FILTER_STATE,
);

export const AIMarketingDataState = atom<AIMarketingParameterData>(
  INIT_AI_MARKETING_DATA_STATE,
);

export const isDollarState = atomWithLocalStorage<boolean>(
  'sales_isDollar',
  false,
);

// Selector를 derived atom으로 변환
export const hasMatchSelector = atomFamily(
  ({
    name,
    email,
    phone,
  }: {
    name: string;
    email?: string | null;
    phone?: string | null;
  }) =>
    atom((get) => {
      const { searchText } = get(orgChartState);
      if (!searchText) return false;
      const q = searchText.toLowerCase();
      return (
        name.toLowerCase().includes(q) ||
        (email ?? '').toLowerCase().includes(q) ||
        (phone ?? '').toLowerCase().includes(q)
      );
    }),
);

export const isCurrSearchSelector = atomFamily(
  ({ passportId }: { passportId: string }) =>
    atom((get) => {
      const { searchPassports, targetSearchIndex } = get(orgChartState);
      const curr = searchPassports[targetSearchIndex - 1]?.passportId;
      return curr === passportId;
    }),
);

export const hasActiveQuerySelector = atom(
  (get) => !!get(orgChartState).searchText?.trim(),
);

/**
 * 업로드 중인 항목. percent 는 바이트 진행률을 아는 경우에만 채운다 (PORTAL-12593).
 * 녹음 파일을 문서에 연결하는 단계처럼 전송이 이미 끝난 작업은 percent 없이 스피너만 보여준다.
 */
export type UploadingItem = { name: string; percent?: number };

export const uploadingState = atom<{
  file: UploadingItem[];
  record: UploadingItem[];
}>({
  file: [],
  record: [],
});
